import { useMemo, useState } from 'react';
import { formatEodSwap, formatNumber } from '@/utils/format';
import type { EodNetInstrumentDto } from '@/api/types/riskDashboard';

const ZERO = 0.00005;

function addNullable(a?: number | null, b?: number | null): number | null {
  if (a == null && b == null) return null;
  return (a ?? 0) + (b ?? 0);
}

// Signed +/- swap span (mirrors EodSwapHtmlFormatters.FormatSignedHtml).
function swapCell(v?: number | null) {
  const f = formatEodSwap(v);
  return <span className={f.cls}>{f.text}</span>;
}

// FormatPercentHtml multiplies the fraction by 100.
function pctCell(v?: number | null) {
  if (v == null || Number.isNaN(v)) return <span className="text-muted">NA</span>;
  return <span>{formatNumber(v * 100, 2)}%</span>;
}

interface SideRow {
  side: 'long' | 'short';
  instrument: string;
  clientSwap?: number | null;
  lpSwap?: number | null;
  netSwap: number | null;
  concentration?: number | null;
}

// Nested Net child row for the Instruments Exposure table: Long/Short pill toggle
// + a searchable per-side table. Mirrors the server-rendered EodSwapNetChild partial
// (Views/Shared/Components/EodSwapNetChild/Default.cshtml) + the searchable DataTable
// legacy risk-dashboard-exposure.js builds (dom:'t', searching:true) when the
// EOD-swap value is clicked.
export function EodSwapNetChild({ eod }: { eod: EodNetInstrumentDto }) {
  const hasLong =
    (eod.clientLongSwap != null && Math.abs(eod.clientLongSwap) >= ZERO) ||
    (eod.lpLongSwap != null && Math.abs(eod.lpLongSwap) >= ZERO);
  const [side, setSide] = useState<'long' | 'short'>(hasLong ? 'long' : 'short');
  const [search, setSearch] = useState('');

  const row: SideRow =
    side === 'long'
      ? {
          side: 'long',
          instrument: eod.instrument,
          clientSwap: eod.clientLongSwap,
          lpSwap: eod.lpLongSwap,
          netSwap: addNullable(eod.clientLongSwap, eod.lpLongSwap),
          concentration: eod.concentration,
        }
      : {
          side: 'short',
          instrument: eod.instrument,
          clientSwap: eod.clientShortSwap,
          lpSwap: eod.lpShortSwap,
          netSwap: addNullable(eod.clientShortSwap, eod.lpShortSwap),
          concentration: eod.concentration,
        };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [row];
    const hay = [
      row.instrument,
      formatEodSwap(row.clientSwap).text,
      formatEodSwap(row.lpSwap).text,
      formatEodSwap(row.netSwap).text,
      row.concentration != null ? `${formatNumber(row.concentration * 100, 2)}%` : '',
    ]
      .join(' ')
      .toLowerCase();
    return hay.includes(q) ? [row] : [];
  }, [row, search]);

  const pill = (value: 'long' | 'short', label: string) => (
    <button
      type="button"
      className={`btn btn-sm rounded-pill eod-child-filter me-1 ${side === value ? 'btn-primary active' : 'btn-outline-primary'}`}
      onClick={() => setSide(value)}
    >
      {label}
    </button>
  );

  return (
    <div className="nop-table-wrapper rd-child-panel">
      <div className="p-2">
        <div className="eod-entity-section mt-1">
          <div className="d-flex align-items-center mb-1">
            <h5 className="fw-bold mb-0 ms-2">Net</h5>
            <div className="eod-pill-group btn-group ms-2" role="group">
              {pill('long', 'Long')}
              {pill('short', 'Short')}
            </div>
            <input
              type="search"
              className="form-control form-control-sm w-auto ms-auto"
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <table className="details-container eod-child-table table table-hover text-center my-1 mx-0 w-100">
            <thead>
              <tr className="custom-table-header">
                <th>Instrument</th>
                <th>Client Swap</th>
                <th>LP Swap</th>
                <th>Expected EOD Swap</th>
                <th>Concentration</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center text-muted py-2">
                    No data available
                  </td>
                </tr>
              ) : (
                visible.map((r) => (
                  <tr className="details-row" data-side={r.side} key={r.side}>
                    <td>{r.instrument}</td>
                    <td>{swapCell(r.clientSwap)}</td>
                    <td>{swapCell(r.lpSwap)}</td>
                    <td>{swapCell(r.netSwap)}</td>
                    <td>{pctCell(r.concentration)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
